import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";

/**
 * Drop-in replacement for native `<select>` — fully styled to the Shariti dark theme.
 * Options receive a max-height + scroll, hover/active states use orange, and the dropdown
 * closes on outside click or Escape.
 *
 * Iter-30 — Le menu est désormais rendu via un Portal (React `createPortal`),
 * positionné en `fixed` avec auto-flip (haut/bas) selon l'espace disponible.
 * Cela évite que le dropdown soit "chevauché" / clippé par un parent qui a
 * `overflow-hidden`, `transform`, ou `backdrop-filter` (cas typique des
 * glass-cards et des modales).
 *
 * Props:
 *   - options: Array<{ value: string, label: string, group?: string }>
 *   - value: string
 *   - onChange: (newValue: string) => void
 *   - placeholder?: string
 *   - size?: "sm" | "md" (default "md")
 *   - disabled?: boolean
 *   - className?: string  (applied to the trigger button)
 *   - dataTestid?: string
 */
export default function ThemedSelect({
    options = [],
    value,
    onChange,
    placeholder = "Sélectionner...",
    size = "md",
    disabled = false,
    className = "",
    dataTestid,
}) {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const selected = options.find((o) => o.value === value);

    // Iter-27.57 — Regroupement par `group` si présent (rétrocompatible)
    const grouped = (() => {
        const hasGroups = options.some((o) => o.group);
        if (!hasGroups) return null;
        const order = [];
        const map = {};
        options.forEach((o) => {
            const g = o.group || "—";
            if (!map[g]) {
                map[g] = [];
                order.push(g);
            }
            map[g].push(o);
        });
        return order.map((g) => ({ name: g, items: map[g] }));
    })();

    // Iter-30 — Calcul de la position du menu portal (fixed) avec auto-flip
    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return undefined;
        const compute = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            const menuMaxH = 320;
            const gap = 6;
            const spaceBelow = window.innerHeight - rect.bottom - 8;
            const spaceAbove = rect.top - 8;
            const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
            setMenuStyle({
                position: "fixed",
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                ...(openUp
                    ? {
                          bottom: `${window.innerHeight - rect.top + gap}px`,
                          maxHeight: `${Math.min(menuMaxH, spaceAbove - 8)}px`,
                      }
                    : {
                          top: `${rect.bottom + gap}px`,
                          maxHeight: `${Math.min(menuMaxH, spaceBelow - 8)}px`,
                      }),
                zIndex: 9999,
            });
        };
        compute();
        window.addEventListener("scroll", compute, true);
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute, true);
            window.removeEventListener("resize", compute);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const sizeClasses =
        size === "sm"
            ? "px-3 py-1.5 text-xs"
            : "px-4 py-3 text-sm";

    return (
        <div className="relative w-full">
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                data-testid={dataTestid}
                className={`w-full flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-2xl ${sizeClasses} text-white text-left outline-none transition-all hover:border-shariti-orange/50 focus:border-shariti-orange/60 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            >
                <span className={`truncate font-bold ${selected ? "text-white" : "text-white/40"}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-white/60 shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {open && typeof document !== "undefined" && createPortal(
                <AnimatePresence>
                    <motion.div
                        ref={menuRef}
                        initial={{ opacity: 0, y: -6, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.98 }}
                        transition={{ duration: 0.12 }}
                        style={menuStyle}
                        className="rounded-2xl border border-shariti-orange/30 bg-shariti-black/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden"
                        data-testid={dataTestid ? `${dataTestid}-menu` : undefined}
                    >
                        <div className="overflow-y-auto p-1" style={{ maxHeight: menuStyle.maxHeight }}>
                            {options.length === 0 && (
                                <div className="px-3 py-2 text-xs text-white/40">Aucune option</div>
                            )}
                            {grouped ? (
                                grouped.map((g) => (
                                    <div key={g.name} className="pb-1">
                                        <div className="px-3 pt-2 pb-1 text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-orange-light/70">
                                            {g.name}
                                        </div>
                                        {g.items.map((opt) => {
                                            const isSelected = opt.value === value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => {
                                                        onChange?.(opt.value);
                                                        setOpen(false);
                                                    }}
                                                    data-testid={
                                                        dataTestid ? `${dataTestid}-option-${opt.value}` : undefined
                                                    }
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors text-left ${
                                                        isSelected
                                                            ? "bg-shariti-orange/20 text-shariti-orange-light"
                                                            : "text-white/85 hover:bg-white/5 hover:text-white"
                                                    }`}
                                                >
                                                    <span className="truncate">{opt.label}</span>
                                                    {isSelected && <Check className="w-4 h-4 shrink-0" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))
                            ) : (
                                options.map((opt) => {
                                    const isSelected = opt.value === value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => {
                                                onChange?.(opt.value);
                                                setOpen(false);
                                            }}
                                            data-testid={
                                                dataTestid ? `${dataTestid}-option-${opt.value}` : undefined
                                            }
                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors text-left ${
                                                isSelected
                                                    ? "bg-shariti-orange/20 text-shariti-orange-light"
                                                    : "text-white/85 hover:bg-white/5 hover:text-white"
                                            }`}
                                        >
                                            <span className="truncate">{opt.label}</span>
                                            {isSelected && <Check className="w-4 h-4 shrink-0" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>,
                document.body,
            )}
        </div>
    );
}
